import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../color-palette/colors';
import { IconButton } from '../Buttons';
import { visuallyHiddenSx } from '../Fields/fieldStyles';

export type BreadcrumbNamesPage = 'always' | 'desktop';

export interface ScreenHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  actions?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
  headingLevel?: 1 | 2 | 3;
  breadcrumbNamesPage?: BreadcrumbNamesPage;
  sx?: SxProps<Theme>;
}

const textSx = { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 } as const;
const headingBlockSx = { display: 'flex', flexDirection: 'column', gap: 1 } as const;
const titleSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontSize: { xs: theme.typography.h1.fontSize, sm: theme.typography.title.fontSize },
  fontWeight: theme.typography.title.fontWeight,
  lineHeight: theme.typography.lineHeightHeading,
  textWrap: 'balance',
  overflowWrap: 'anywhere',
  '&:focus': { outline: 'none' },
});
const subtitleSx = { color: Neutrals[700] } as const;
const actionsSx = { flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } } as const;
const backSx = { display: { md: 'none' } } as const;

const hiddenFrom = (where: BreadcrumbNamesPage) => (theme: Theme): SystemStyleObject<Theme> =>
  where === 'always' ? visuallyHiddenSx : { [theme.breakpoints.up('md')]: visuallyHiddenSx };

export const collapsedWhere = (where: BreadcrumbNamesPage | undefined, visibleOnPhone: boolean, visibleOnDesktop: boolean): BreadcrumbNamesPage | null => {
  if (where === undefined || visibleOnDesktop) return null;
  return where === 'always' && !visibleOnPhone ? 'always' : 'desktop';
};

export const ScreenHeader = React.forwardRef<HTMLDivElement, ScreenHeaderProps>(
  ({ title, subtitle, breadcrumb, actions, onBack, backLabel, headingLevel = 1, breadcrumbNamesPage, sx }, ref) => {
    const { t } = useTranslation();
    const visibleOnDesktop = Boolean(subtitle) || Boolean(actions) || Boolean(breadcrumb);
    const collapsed = collapsedWhere(breadcrumbNamesPage, visibleOnDesktop || Boolean(onBack), visibleOnDesktop);
    return (
      <Stack
        ref={ref}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={[collapsed !== null && hiddenFrom(collapsed), ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          {onBack && (
            <IconButton onClick={onBack} label={backLabel ?? t('common.goBack', 'Go Back')} edge="start" sx={backSx}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box sx={textSx}>
            {breadcrumb}
            <Box sx={headingBlockSx}>
              <Typography variant="title" component={`h${headingLevel}`} tabIndex={-1} sx={[titleSx, breadcrumbNamesPage !== undefined && hiddenFrom(breadcrumbNamesPage)]}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body" component="p" sx={subtitleSx}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
        {actions && <Box sx={actionsSx}>{actions}</Box>}
      </Stack>
    );
  },
);

ScreenHeader.displayName = 'ScreenHeader';
